import { CommonModule } from '@angular/common';
import { Component, signal, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FamilyService } from '../../core/families/family.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { JoinFamilyResult } from '../../api/generated';

@Component({
  selector: 'app-family-access-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatTabsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule
  ],
  templateUrl: './family-access-dialog.component.html',
  styleUrls: ['./family-access-dialog.component.scss']
})
export class FamilyAccessDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<FamilyAccessDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly familyService = inject(FamilyService);
  private readonly snackBar = inject(MatSnackBar);

  readonly createForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.minLength(3)]]
  });

  readonly joinForm = this.fb.nonNullable.group({
    code: ['', [Validators.required, Validators.minLength(4)]]
  });

  readonly busy = signal(false);
  readonly error = signal<string | null>(null);

  closeIfPossible() {
    this.dialogRef.close();
  }

  async createFamily() {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    this.busy.set(true);
    try {
      const family = await this.familyService.createFamily(this.createForm.value.name!.trim());
      this.snackBar.open(`Created ${family.name}`, undefined, { duration: 2000 });
      this.dialogRef.close(family);
    } catch (error) {
      this.error.set((error as Error).message ?? 'Could not create family.');
    } finally {
      this.busy.set(false);
    }
  }

  async joinFamily() {
    if (this.joinForm.invalid) {
      this.joinForm.markAllAsTouched();
      return;
    }
    this.busy.set(true);
    try {
      const code = this.joinForm.value.code!.trim();
      const result = await this.familyService.requestJoin(code);
      if (result.result === JoinFamilyResult.AlreadyMember && result.family) {
        this.snackBar.open(`Joined ${result.family.name}`, undefined, { duration: 2000 });
        this.dialogRef.close(result.family);
        return;
      }

      if (result.result === JoinFamilyResult.RequestCreated) {
        this.error.set(null);
        this.snackBar.open('Request sent. A parent will need to approve it.', undefined, { duration: 3500 });
        this.joinForm.reset();
        return;
      }

      if (result.result === JoinFamilyResult.RequestPending) {
        this.error.set(null);
        this.snackBar.open('You already have a pending request for that family.', undefined, { duration: 3500 });
        return;
      }

      this.error.set('No family with that code. Ask for a new invite code.');
    } catch (error) {
      this.error.set((error as Error).message ?? 'Could not join family.');
    } finally {
      this.busy.set(false);
    }
  }
}
